/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPaginationStyles {
    wrapper?: InjectableCss;
    infoMessage?: TypographyPresentationProps;
}

export const paginationStyles: ProductListPaginationStyles = {
    infoMessage: {
        color: "text.main",
        size: 12,
    },
};

const styles = paginationStyles;

const ProductListPagination = ({ pagination }: Props) => {
    if (!pagination) {
        return null;
    }

    const { currentPage, pageSize, totalItemCount, numberOfPages } = pagination as PaginationModel;

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.infoMessage}>
                {translate(
                    "Showing {0}-{1} of {2} Products",
                    `${(currentPage - 1) * pageSize + 1}`,
                    `${currentPage !== numberOfPages ? currentPage * pageSize : totalItemCount}`,
                    `${totalItemCount}`,
                )}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination Info",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
